import { observer } from 'mobx-react-lite'
import { AssessmentResult, Result } from '@almanaclabs/yoastseo/dist/index.js'
import { PropsWithChildren, useState } from 'react'
import Styles from './SEOAnalysis.module.scss'
import { Input } from 'almanac-ui/TextInputField/Input'
import { ReactComponent as Check } from 'assets/icons/checkmark12.svg'
import { ReactComponent as Reject } from 'assets/icons/reject12.svg'
import { ReactComponent as Triangle } from 'assets/icons/warning16.svg'
import cx from 'classnames'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { ChevronRight } from 'blaze-ui/icons/24'
import { AlertTriangle } from 'blaze-ui/icons'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useSEOAnalysis } from './hooks/useSEOAnalysis'
import { SEOScoreGauge } from './SEOScoreGauge/SEOScoreGauge'
import { useDebouncedContentAnalysis } from './hooks/useDebouncedContentAnalysis'

enum SEOOutcome {
  good = 'good',
  warning = 'warning',
  problem = 'problem',
}

const SEOResultIcons = {
  [SEOOutcome.good]: <Check />,
  [SEOOutcome.warning]: <Triangle />,
  [SEOOutcome.problem]: <Reject />,
}

const extractText = (htmlString: string): { url: string; text: string; title: string } => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlString, 'text/html')
  const titleElement = doc.querySelector('a')
  const title = titleElement?.textContent || ''
  const url = titleElement?.href || ''
  const text = (doc.body.textContent || '').replace(title, '').trim().replace(/^:\s*/, '')

  return { url, text, title }
}

export const SEOAnalysisError = ({ keyword }: { keyword: string }) => {
  return (
    <div className={Styles.errorContainer}>
      <AlertTriangle size={24} color="var(--red-70)" />
      <Heading level={4}>Oops! Something went wrong.</Heading>
      <Text>
        {keyword
          ? 'Try removing your keyword entirely and analyze again.'
          : 'Our team has been notified and we are looking into it.'}
      </Text>
    </div>
  )
}

const SEOAnalysisResults = ({ results }: { results: Result }) => {
  return (
    <div className={Styles.resultCategories}>
      <SEOScoreGauge analysisResults={{ results: results, error: false }} className={Styles.seoScore} />
      <ResultCategory title="SEO" results={results.seo[''].results} />
      <ResultCategory title="Readability" results={results.readability.results} />
    </div>
  )
}

const replaceKeyphrasesWithKeywords = (text: string): string =>
  text
    .replace(/\bKeyphrases\b/g, 'Keywords')
    .replace(/\bkeyphrases\b/g, 'keywords')
    .replace(/\bKeyphrase\b/g, 'Keyword')
    .replace(/\bkeyphrase\b/g, 'keyword')

const ResultItem = ({ result }: { result: AssessmentResult }) => {
  let outcome: SEOOutcome = SEOOutcome.problem
  if (result.score >= 9) {
    outcome = SEOOutcome.good
  } else if (result.score >= 6) {
    outcome = SEOOutcome.warning
  }

  const { url, text, title } = extractText(result.text)

  return (
    <div className={Styles.resultItem}>
      <div className={Styles.resultHeader}>
        <span className={Styles.resultIcon}>{SEOResultIcons[outcome]}</span>
        <Text variant="smallList">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={cx({
              [Styles.good]: outcome === 'good',
              [Styles.problem]: outcome === SEOOutcome.problem,
              [Styles.warning]: outcome === SEOOutcome.warning,
            })}
          >
            {replaceKeyphrasesWithKeywords(title)}
          </a>
        </Text>
      </div>
      <span className={Styles.resultContent}>{replaceKeyphrasesWithKeywords(text)}</span>
    </div>
  )
}

const ResultList = ({ results }: { results: AssessmentResult[] }) => {
  return (
    <div className={Styles.resultsList}>
      {results
        .filter((r) => !!r.text)
        .map((result) => (
          <ResultItem result={result} key={result._identifier} />
        ))}
    </div>
  )
}

const ResultCategory = ({ title, results }: { title: string; results: AssessmentResult[] }) => {
  const [expanded, setExpanded] = useState(false)

  const problemAndWarningTotal: { problem: number; warning: number } = results.reduce(
    (acc, result) => {
      if (result.score === 0) {
        return acc
      }
      if (result.score < 6) {
        acc.problem++
      } else if (result.score < 9) {
        acc.warning++
      }
      return acc
    },
    { problem: 0, warning: 0 },
  )

  return (
    <div className={Styles.resultCategory}>
      <div className={Styles.resultCategoryHeader} onClick={() => setExpanded(!expanded)}>
        <Text variant="largeList">
          <span className={Styles.resultCategoryTitle}>{title}</span>
        </Text>
        <div className={Styles.results}>
          {problemAndWarningTotal.problem === 0 && problemAndWarningTotal.warning === 0 && (
            <span className={cx(Styles.resultCategoryScore, Styles.good)}>
              <Check />
            </span>
          )}
          {problemAndWarningTotal.problem > 0 && (
            <span className={cx(Styles.resultCategoryScore, Styles.problem)}>
              {problemAndWarningTotal.problem} problem{problemAndWarningTotal.problem > 1 ? 's' : ''}
            </span>
          )}
          {problemAndWarningTotal.warning > 0 && (
            <span className={cx(Styles.resultCategoryScore, Styles.warning)}>
              {problemAndWarningTotal.warning} warning{problemAndWarningTotal.warning > 1 ? 's' : ''}
            </span>
          )}
          <div className={cx(Styles.chevron, { [Styles.expanded]: expanded })}>
            <ChevronRight />
          </div>
        </div>
      </div>
      {expanded && <ResultList results={results} />}
    </div>
  )
}

const SEOAnalysis = observer(({ children }: PropsWithChildren) => {
  const [initialized, setInitialized] = useState(false)

  const { analyze, error, results, keyword, setKeyword } = useSEOAnalysis()

  useDebouncedContentAnalysis(analyze)

  if (!initialized) {
    setInitialized(true)
    analyze()
  }

  let content

  if (results) {
    content = <SEOAnalysisResults results={results} />
  }

  if (error) {
    content = <SEOAnalysisError keyword={keyword} />
  }

  return (
    <div className={Styles.container}>
      <div className={Styles.descriptionContainer}>
        <Text variant="secondary">
          <span className={Styles.description}>
            Elevate your website's visibility. Get actionable insights for better search engine rankings and online
            growth.
          </span>
        </Text>
        <div className={Styles.keyphraseContainer}>
          <div className={Styles.inputContainer}>
            <span className={Styles.inputLabel}>Focus Keyword</span>
            <Input
              size="lg"
              name="keyphrase"
              value={keyword}
              onChange={(e) => setKeyword(e.currentTarget.value)}
              placeholder="Enter optional keyword"
            />
          </div>
          <Button variant="secondary" size="md" onPress={analyze}>
            Analyze
          </Button>
        </div>
      </div>
      {content}
      {children}
    </div>
  )
})

export default SEOAnalysis
